import * as Yup from "yup";
import { validatePhoneNumber } from "../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { CountryOption } from "../../../components/PhoneNumberCountryCodeSelector/utils/types";
import { levelOfStudyOptions } from "../../../utils/constants/levelOfStudyOptions";

export interface FormContext {
  isEdit: boolean;
  isCampaign: boolean;
  isDowning: boolean;
  isVfAdmin: boolean;
  validations: string[];
}

export interface FieldConfig {
  isVisible: boolean;
  isRequired: boolean;
}

export interface CreateEditLeadFormConfig {
  name: FieldConfig;
  email: FieldConfig;
  phone: FieldConfig;
  levelOfStudy: FieldConfig;
  tenantType: FieldConfig;
  utmSource: FieldConfig;
  tenantStudySection: FieldConfig;
  minBudget: FieldConfig;
  maxBudget: FieldConfig;
  stayDuration: FieldConfig;
  moveInDate: FieldConfig;
}

const createPhoneValidation = () => {
  return Yup.string().test(
    "valid-phone",
    "Please enter a valid phone number",
    function (value) {
      if (!value || !this.parent.phoneCountry) return true;
      const phoneData = validatePhoneNumber(value, this.parent.phoneCountry);
      return phoneData.isValid;
    }
  );
};

const createWhatsappValidation = () => {
  return Yup.string().test(
    "valid-whatsapp",
    "Please enter a valid WhatsApp number",
    function (value) {
      if (!value || !this.parent.whatsappCountry) return true;
      const phoneData = validatePhoneNumber(value, this.parent.whatsappCountry);
      return phoneData.isValid;
    }
  );
};

const getBaseSchema = () => ({
  destinationCountry: Yup.string().nullable(),
  destinationCity: Yup.string().nullable(),
  university: Yup.string().nullable(),
  roomType: Yup.array().of(Yup.string()).nullable(),
  whatsappNumber: createWhatsappValidation(),
  move_in_date: Yup.date().nullable(),
});

export const createValidationSchema = (context: FormContext) => {
  const baseSchema = getBaseSchema();

  if (context.isEdit && context.isVfAdmin) {
    return Yup.object(baseSchema);
  }

  const nameValidation = context.isEdit
    ? Yup.string()
    : Yup.string().required("Name is required");

  const emailValidation = context.isEdit
    ? Yup.string().email("Invalid email format")
    : Yup.string()
        .email("Invalid email format")
        .test(
          "at-least-one-contact",
          "At least one of email, phone number, or WhatsApp number is required",
          function (value) {
            const hasEmail = value && value.trim() !== "";
            const hasPhone =
              this.parent.phoneNumber &&
              this.parent.phoneNumber.trim() !== "" &&
              this.parent.phoneCountry !== null;
            const hasWhatsapp =
              this.parent.whatsappNumber &&
              this.parent.whatsappNumber.trim() !== "" &&
              this.parent.whatsappCountry !== null;

            return hasEmail || hasPhone || hasWhatsapp;
          }
        );

  const phoneValidation = Yup.string().when("phoneCountry", {
    is: (phoneCountry: CountryOption | null) => phoneCountry !== null,
    then: () => createPhoneValidation(),
    otherwise: (schema) => schema,
  });

  const whatsappValidation = Yup.string().when("whatsappCountry", {
    is: (whatsappCountry: CountryOption | null) => whatsappCountry !== null,
    then: () => createWhatsappValidation(),
    otherwise: (schema) => schema,
  });

  const levelOfStudyValidation =
    context.isDowning && !context.isEdit
      ? Yup.string()
          .nullable()
          .when("tenantType", {
            is: (tenantType: string) => tenantType !== "working professional",
            then: (schema) => schema.required("Level of study is required"),
            otherwise: (schema) => schema.nullable(),
          })
      : Yup.string().nullable();

  const tenantTypeValidation =
    context.isDowning && !context.isEdit
      ? Yup.string().required("Tenant type is required")
      : Yup.string().nullable();

  const utmSourceValidation = context.isEdit
    ? Yup.string().nullable()
    : context.isDowning
    ? Yup.string().required("UTM source is required")
    : Yup.string().nullable();

  const minBudgetValidation =
    context.isDowning && !context.isEdit
      ? Yup.string().required("Minimum budget is required")
      : Yup.string();

  const maxBudgetValidation =
    context.isDowning && !context.isEdit
      ? Yup.string().required("Maximum budget is required")
      : Yup.string();

  const stayDurationValidation =
    context.isDowning && !context.isEdit
      ? Yup.string().required("Stay duration is required")
      : Yup.string();

  return Yup.object({
    ...baseSchema,
    name: nameValidation,
    email: emailValidation,
    phoneNumber: phoneValidation,
    whatsappNumber: whatsappValidation,
    levelOfStudy: levelOfStudyValidation,
    tenantType: tenantTypeValidation,
    utmSource: utmSourceValidation,
    min_budget: minBudgetValidation,
    max_budget: maxBudgetValidation,
    stay_duration: stayDurationValidation,
  });
};

export const createEditLeadFormConfig = (
  context: FormContext
): CreateEditLeadFormConfig => {
  const emailPhoneVisible = !(context.isVfAdmin && context.isEdit);
  const utmSourceVisible =
    !context.isCampaign && (!context.isEdit || context.isVfAdmin);
  const tenantStudySectionVisible = !context.isCampaign;
  const budgetFieldsVisible = !context.isCampaign;

  return {
    name: {
      isVisible: true,
      isRequired: !context.isEdit,
    },
    email: {
      isVisible: emailPhoneVisible,
      isRequired: false,
    },
    phone: {
      isVisible: emailPhoneVisible,
      isRequired: false,
    },
    levelOfStudy: {
      isVisible: tenantStudySectionVisible,
      isRequired: context.isDowning && !context.isEdit,
    },
    tenantType: {
      isVisible: tenantStudySectionVisible,
      isRequired: context.isDowning && !context.isEdit,
    },
    utmSource: {
      isVisible: utmSourceVisible,
      isRequired: context.isDowning && !context.isEdit,
    },
    tenantStudySection: {
      isVisible: tenantStudySectionVisible,
      isRequired: false,
    },
    minBudget: {
      isVisible: budgetFieldsVisible,
      isRequired: context.isDowning && !context.isEdit,
    },
    maxBudget: {
      isVisible: budgetFieldsVisible,
      isRequired: context.isDowning && !context.isEdit,
    },
    stayDuration: {
      isVisible: budgetFieldsVisible,
      isRequired: context.isDowning && !context.isEdit,
    },
    moveInDate: {
      isVisible: budgetFieldsVisible,
      isRequired: false,
    },
  };
};

export const getFilteredLevelOfStudyOptions = (tenantType: string | null) => {
  if (tenantType === "student") {
    return levelOfStudyOptions.filter(
      (option) => option.value !== "Non Student"
    );
  }
  if (tenantType === "working professional") {
    return levelOfStudyOptions.filter(
      (option) => option.value === "Non Student"
    );
  }
  return levelOfStudyOptions;
};

export const shouldShowLevelOfStudy = (tenantType: string | null) => {
  return tenantType !== "working professional";
};
